<template>
  <div class="panel-distribuidor tw-min-h-screen tw-w-full tw-bg-[#f8f9fa] tw-py-8">
    <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
      
      <!-- HEADER -->
      <div class="tw-mb-8 tw-flex tw-flex-col md:tw-flex-row md:tw-items-end tw-justify-between tw-gap-4">
        <div>
          <h1 class="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">Herramientas de Distribuidor</h1>
          <p class="tw-text-gray-600">Encuentra aquí todos los cursos de pago, su material promocional y tu enlace de referido para empezar a compartir.</p>
        </div>
        
        <!-- Search Bar -->
        <div class="tw-w-full md:tw-w-72 tw-relative">
          <div class="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
            <i class="fas fa-search tw-text-gray-400"></i>
          </div>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Buscar por nombre..." 
            class="tw-w-full tw-pl-10 tw-pr-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-shadow"
            style="focus:ring-color: var(--primary-color, #18d600);"
          >
        </div>
      </div>

      <!-- LOADING STATE -->
      <div v-if="loading" class="tw-flex tw-justify-center tw-items-center tw-py-20">
        <div class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-emerald-500"></div>
      </div>

      <!-- ERROR STATE -->
      <div v-else-if="error" class="tw-bg-red-50 tw-text-red-600 tw-p-4 tw-rounded-xl tw-text-center">
        Error al cargar los cursos. Por favor, intenta de nuevo más tarde.
      </div>

      <!-- COURSES GRID -->
      <div v-else-if="filteredCourses.length > 0" class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-8">
        <div v-for="course in filteredCourses" :key="course.id" class="tw-bg-white tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-flex tw-flex-col">
          
          <!-- Course Image -->
          <div class="tw-relative tw-w-full tw-pt-[56.25%] tw-bg-gray-100">
            <img :src="getCourseImage(course)" :alt="course.title" class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-object-cover" />
            <div class="tw-absolute tw-top-3 tw-right-3 badge-price">
              ${{ getCoursePrice(course) }}
            </div>
          </div>

          <!-- Course Info -->
          <div class="tw-p-5 tw-flex tw-flex-col tw-flex-grow">
            <h3 class="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-2">{{ course.title }}</h3>
            <p class="tw-text-sm tw-text-gray-600 tw-line-clamp-3 tw-mb-4">{{ course.description }}</p>
            
            <!-- Acción: Publicitar -->
            <button 
              @click="goToDetails(course)"
              class="btn-share tw-mt-auto"
            >
              Publicitar
            </button>
          </div>
        </div>
      </div>

      <!-- EMPTY STATE (SEARCH) -->
      <div v-else class="tw-bg-white tw-border tw-border-gray-200 tw-p-8 tw-rounded-xl tw-text-center tw-text-gray-500">
        <i class="fas fa-search tw-text-4xl tw-mb-3 tw-text-gray-300"></i>
        <p class="tw-text-lg tw-font-semibold">No se encontraron cursos</p>
        <p class="tw-text-sm">Intenta con otros términos de búsqueda.</p>
      </div>

    </div>

    <!-- Reusable Custom Alert (if applicable, else just use a native toast or sweetalert) -->
    <CustomAlert 
      v-if="alert.show"
      :type="alert.type"
      :message="alert.message"
      :defaultTitle="alert.title"
      class="tw-fixed tw-bottom-4 tw-right-4 tw-z-50"
    />
  </div>
</template>

<script>
import { authGet } from "@/helpers/authStorage";
import CustomAlert from "@/components/CustomAlert.vue";

export default {
  name: "PanelDistribuidor",
  components: {
    CustomAlert
  },
  data() {
    return {
      courses: [],
      searchQuery: '',
      loading: true,
      error: false,
      user: null,
      alert: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      }
    };
  },
  computed: {
    filteredCourses() {
      if (!this.searchQuery) return this.courses;
      const query = this.searchQuery.toLowerCase();
      return this.courses.filter(course => {
        return course.title && course.title.toLowerCase().includes(query);
      });
    }
  },
  async created() {
    // Get user from local storage
    const userStr = authGet();
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        console.error("Error parsing user:", e);
      }
    }
    
    await this.fetchPaidCourses();
  },
  methods: {
    async fetchPaidCourses() {
      try {
        this.loading = true;
        // Obtenemos todos los productos del marketplace (cursos, libros, etc.)
        const response = await this.axios.get("marketing/marketplace/courses?per_page=100");
        if (response.data && response.data.data) {
          // Extraemos la data de la respuesta paginada
          const fetchedCourses = response.data.data.data || response.data.data;
          
          // No filtramos por precio mayor a cero para asegurar que aparezcan libros y otros productos
          this.courses = fetchedCourses;
        }
      } catch (err) {
        console.error("Error fetching courses for distributor panel:", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    getCourseImage(course) {
      const url = course.url_portada || course.image || course.url_front_page;
      if (!url) return 'https://via.placeholder.com/640x360?text=Curso';
      
      if (url.startsWith('http')) {
        return url.replace('s3.sa-east-1', 's3-accelerate');
      }
      
      return "https://promolider-storage-user.s3-accelerate.amazonaws.com/" + url;
    },
    getCoursePrice(course) {
      if (course.price) return parseFloat(course.price).toFixed(2);
      if (course.price_base) return parseFloat(course.price_base).toFixed(2);
      return '0.00';
    },
    goToDetails(course) {
      this.$router.push({ name: 'PanelDistribuidorCurso', params: { id: course.id } });
    },
    copyShareLink(course) {
      const idUser = authGet("id_user");
      if (!idUser) {
        this.showAlert('error', 'Error', 'No se pudo obtener tu usuario de referido.');
        return;
      }

      // Estructura del link: /landing/course/{id}?ref={id_user}
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/landing/course/${course.id}?ref=${idUser}`;
      
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.showAlert('success', '¡Enlace Copiado!', `El enlace de referido para "${course.title}" se copió al portapapeles.`);
      }).catch(err => {
        console.error('Error al copiar: ', err);
        this.showAlert('error', 'Error', 'No se pudo copiar el enlace.');
      });
    },
    showAlert(type, title, message) {
      this.alert = { show: true, type, title, message };
      setTimeout(() => {
        this.alert.show = false;
      }, 4000);
    }
  }
};
</script>

<style scoped>
/* Transiciones simples para alertas */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Custom Brand Green Styles */
.badge-price {
  background-color: var(--primary-color, #18d600);
  color: white;
  padding: 0.375rem 0.75rem;
  font-weight: bold;
  font-size: 0.875rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.badge-marketing {
  background-color: rgba(24, 214, 0, 0.15);
  color: #12a100;
  padding: 0.375rem 0.75rem;
  font-weight: 600;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;
}
.badge-marketing:hover {
  background-color: rgba(24, 214, 0, 0.25);
}

.btn-share {
  background-color: #111827; /* gray-900 */
  color: white;
  font-weight: 600;
  padding: 0.75rem 1rem;
  transition: background-color 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
.btn-share:hover {
  background-color: var(--primary-color, #18d600);
}
</style>
